import TimezoneDropdown from './TimezoneDropdown';
import { timezoneItemTempl, timezoneCurrentTime, timezoneDate, timezoneItemDiff, timezoneCurrent, timezoneDropdown } from '../common/template';
import { setDateByTimezone, changeDateByTimezone, getDayPeriod } from './LuxonDateTime';
import { getTimeSelectOption, setTimeSelectOption } from '../common/util';
import { empty } from '../common/dom';
import type EventEmitter from '../common/eventEmitter';
import type { ITimezoneItem } from '../../interface/ITimezone';
import type { DateTime } from 'luxon';
import type { OriginDataType } from '../../interface/IDropdown';

class TimezoneItem {
  $element!: HTMLElement;

  $timezoneCurrent!: HTMLElement;

  data!: ITimezoneItem;

  emitter!: EventEmitter;

  currentTime!: number;

  diffTime!: number;

  dateByTimezone!: DateTime;

  prevDateByTimezone!: DateTime;

  timeDropdown?: TimezoneDropdown;

  constructor({ data, currentTime, emitter }: {data: ITimezoneItem; currentTime: number; emitter: EventEmitter;}) {
    if(!data) {
      console.error('timezone item을 그릴 데이터가 없습니다.');
      return;
    }

    this.data = data;
    this.currentTime = currentTime;
    this.emitter = emitter;
    this.init();
  }

  init() {
    this.initElement();
    this.setDateByTimezone(this.currentTime);
    this.render();
  }

  initElement() {
    const { timezone } = this.data || {};
    this.$element = document.createElement('div');
    this.$element.classList.add('ncui-timezone-item');
    this.$element.insertAdjacentHTML('beforeend', timezoneItemTempl(timezone));
  }

  renderTime() {
    const { minute } = this.dateByTimezone;
    const { dayPeriodHour, dayPeriod } = getDayPeriod(this.dateByTimezone);
    const currentTimeTempl = timezoneCurrentTime(dayPeriodHour, minute, dayPeriod); // TODO. second는 테스트 후 지우기1!!

    this.$timezoneCurrent = this.$element.querySelector('.timezone-current')!;
    this.$timezoneCurrent?.hasChildNodes() && empty(this.$timezoneCurrent);
    this.$timezoneCurrent?.insertAdjacentHTML('beforeend', currentTimeTempl);
  }

  renderDate() {
    const { year, monthShort, day } = this.dateByTimezone;
    const dateTempl = timezoneDate(year, monthShort, day);
    const $timezoneDate = this.$element.querySelector('.timezone-date');

    $timezoneDate?.hasChildNodes() && empty($timezoneDate);
    $timezoneDate?.insertAdjacentHTML('beforeend', dateTempl);
  }

  renderDiffTime() {
    const diffTempl = timezoneItemDiff(this.diffTime);
    this.$element.querySelector('.timezone-title')?.insertAdjacentHTML('beforeend', diffTempl);
  }

  renderDropdown() {
    const option = this.setTimeSelectOption() as OriginDataType;
    const templ = timezoneDropdown();
    this.$element.querySelector('.timezone-time')?.insertAdjacentHTML('beforeend', templ);

    this.timeDropdown = new TimezoneDropdown({
      element: this.$element,
      name: this.data?.timezone,
      data: option,
      changeCallback: (activeData) => {
        const [hour, minute] = activeData?.d1?.split(':');
        const changeTime = changeDateByTimezone(this.dateByTimezone, parseInt(hour, 10), parseInt(minute, 10), this.data.timezone);
        this.emitter?.emit('CHANGE_CURRENT_TIME', changeTime);
      }
    });
  }

  renderTimezoneCurrent() {
    const templ = timezoneCurrent();
    this.$element.querySelector('.timezone-time')?.insertAdjacentHTML('beforeend', templ);
  }

  render() {
    const { useTimeDiff, useTimeSelect } = this.data || {};

    if(useTimeSelect) {
      this.renderDropdown();
      this.timeDropdown?.updateDropdown(this.dateByTimezone);
    } else {
      this.renderTimezoneCurrent();
    }
    
    this.renderTime();
    this.renderDate();

    if(useTimeDiff) {
      this.setDiffTime();
      this.renderDiffTime();
    }
  }

  update(updateTime: number) {
    const { useTimeSelect } = this.data || {};

    this.setDateByTimezone(updateTime);

    if(this.prevDateByTimezone?.day !== this.dateByTimezone?.day) {
      this.renderDate();
    }

    if(useTimeSelect) {
      this.timeDropdown?.updateDropdown(this.dateByTimezone);
    }

    this.renderTime();
  }

  setDateByTimezone(date: number) {
    this.prevDateByTimezone = this.dateByTimezone;

    this.dateByTimezone = setDateByTimezone(date || this.currentTime, this.data?.timezone);
  }

  setDiffTime() {
    const { offset } = this.dateByTimezone;
    this.diffTime = Math.floor(offset / 60);
  }

  setTimeSelectOption() {
    if(!Array.isArray(this.data?.timeSelectOption)) {
      return getTimeSelectOption();
    }

    return this.data?.timeSelectOption?.map(v => setTimeSelectOption(v));
  }

  get element() {
    return this.$element;
  }
}

export default TimezoneItem;
